import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BackgroundAnimation from '../components/BackgroundAnimation';
import api, { resetAuthState } from '../components/api';
import useGlobale from '../components/global';
import { toast } from 'react-toastify';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, authenticated_bschool, hideAuthModal } = useGlobale();
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    if (authenticated_bschool) {
      // Utiliser setTimeout pour éviter les conflits de navigation
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
    }
  }, [authenticated_bschool, navigate]);

  // Charger les credentials sauvegardés
  useEffect(() => {
    try {
      const savedCredentials = localStorage.getItem('credentials');
      if (savedCredentials) {
        const { username, rememberMe } = JSON.parse(savedCredentials);
        if (rememberMe && username) {
          setCredentials(prev => ({ ...prev, username, rememberMe }));
        }
      }
    } catch (e) {
      // Ignore les erreurs de parsing
    }
  }, []);

  // Fermer le modal d'authentification si on arrive sur la page de login
  useEffect(() => {
    hideAuthModal();
  }, [hideAuthModal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCredentials({
      ...credentials,
      [name]: type === 'checkbox' ? checked : value
    });
    // Réinitialiser les erreurs lorsque l'utilisateur modifie les champs
    setError('');
    setNetworkError(false);
  };

  const validateForm = () => {
    if (!credentials.username.trim()) {
      setError("Le nom d'utilisateur est requis");
      return false;
    }
    if (!credentials.password) {
      setError("Le mot de passe est requis");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    setNetworkError(false);
    
    try {
      const response = await api.post('/utilisateurs/signin/', {
        username: credentials.username,
        password: credentials.password
      });
      
      const { user, tokens } = response.data;
      
      // Sauvegarder les credentials si "Se souvenir de moi" est coché
      if (credentials.rememberMe) {
        localStorage.setItem('credentials', JSON.stringify({
          username: credentials.username,
          rememberMe: true
        }));
      } else {
        // Sauvegarder uniquement le nom d'utilisateur pour le modal
        localStorage.setItem('credentials', JSON.stringify({
          username: credentials.username,
          rememberMe: false
        }));
      }
      
      // Réinitialiser l'état d'authentification de l'API AVANT le login
      resetAuthState();
      
      // Utiliser le gestionnaire d'état global pour l'authentification
      await login(user, tokens);
      
      toast.success('Connexion réussie !');
      
      // Utiliser setTimeout pour éviter les conflits de navigation
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
    } catch (err) {
      if (!err.response) {
        setNetworkError(true);
        setError("Impossible de se connecter au serveur. Vérifiez votre connexion internet.");
      } else if (err.response.status === 401) {
        // Afficher l'erreur spécifique du backend si disponible
        if (err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError("Identifiants incorrects. Veuillez vérifier votre nom d'utilisateur et mot de passe.");
        }
        toast.error('Identifiants incorrects');
      } else if (err.response.status === 403) {
        if (err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError("Votre compte est désactivé. Veuillez contacter l'administrateur.");
        }
        toast.error('Votre compte est désactivé');
      } else if (err.response.status === 400) {
        if (err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError("Données de formulaire invalides. Veuillez vérifier vos informations.");
        }
        toast.error('Données de formulaire invalides');
      } else if (err.response.data && err.response.data.error) {
        setError(err.response.data.error);
        toast.error('Une erreur est survenue lors de la connexion');
      } else {
        setError("Une erreur s'est produite. Veuillez réessayer ultérieurement.");
        toast.error('Une erreur est survenue lors de la connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = () => {
    navigate('/reset-password');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <BackgroundAnimation />
      <div className="w-full max-w-md transform transition-all duration-300 hover:scale-[1.02] relative z-10">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden">
          {/* En-tête */}
          <div className="bg-[#202654] p-6 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-white opacity-5"></div>
            <div className="relative">
              <div className="flex justify-center mb-3">
                <div className="h-16 w-16 bg-white/10 rounded-full flex items-center justify-center shadow-lg transform transition-transform duration-300 hover:scale-110">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-white text-4xl font-extrabold tracking-tight mb-2 font-['Montserrat'] uppercase">B-School</h1>
              <p className="text-white/90 text-lg font-medium tracking-wider font-['Montserrat'] uppercase">Système de Gestion de Scolarité</p>
            </div>
          </div>
          
          {/* Formulaire */}
          <div className="p-6">
            <h2 className="text-gray-800 text-lg font-semibold mb-4">Connexion à votre compte</h2>
            
            {error && (
              <div className="mb-4 bg-rose-50 text-rose-600 p-3 rounded-xl text-sm border border-rose-100 animate-shake">
                {error}
              </div>
            )}
            
            {networkError && (
              <div className="mb-4 bg-amber-50 text-amber-600 p-3 rounded-xl text-sm border border-amber-100">
                <p className="font-medium">Problème de connexion au serveur</p>
                <p className="mt-1">Vérifiez votre connexion internet ou contactez l'administrateur système.</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom d'utilisateur <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={credentials.username}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-all duration-200"
                    placeholder="Nom d'utilisateur"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={credentials.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-all duration-200"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={credentials.rememberMe}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#202654] focus:ring-[#202654] border-gray-300 rounded transition-colors duration-200"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                    Se souvenir de moi
                  </label>
                </div>
                
                <div>
                  <button 
                    type="button"
                    onClick={handlePasswordReset}
                    className="text-sm font-medium text-[#202654] hover:text-[#202654]/80 transition-colors duration-200">
                    Mot de passe oublié?
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white transition-all duration-200 ${
                  loading 
                    ? 'bg-[#202654]/60 cursor-not-allowed' 
                    : 'bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transform hover:scale-[1.02]'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connexion en cours...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>
          </div>
          
          {/* Pied de page */}
          <div className="bg-gray-50 px-6 py-4 text-center text-sm text-gray-500 border-t">
            <p>© {new Date().getFullYear()} B-School. Tous droits réservés.</p>
          </div>
        </div>
        
        {/* Lien d'aide */}
        <div className="mt-4 text-center">
          <p className="text-sm text-white">
            Besoin d'aide? <a href="#" className="font-medium text-white/80 hover:text-white transition-colors duration-200">Contactez le support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
